import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { solcellerFaq, solcellerSections } from "@/data/nationella-guider";

const URL = "https://www.akhusbilfritid.se/solceller-husbil";

export const Route = createFileRoute("/solceller-husbil")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(solcellerFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Solceller & litiumbatteri i husbil – dimensionering och priser" },
      {
        name: "description",
        content:
          "Så dimensionerar du solceller och litiumbatteri i husbil: elbehov i Wh, vad 200 W ger per månad i Sverige, AGM mot LiFePO4 och priser monterat.",
      },
      {
        name: "keywords",
        content:
          "solceller husbil, litiumbatteri husbil, solpanel husbil, mppt husbil, dc-dc laddare husbil, agm eller litium husbil, elbehov husbil",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Solceller & litiumbatteri i husbil – dimensionering och priser" },
      {
        property: "og:description",
        content:
          "Wattimmar, verkligt solutbyte månad för månad, AGM mot litium och vad installationen kostar. Från vår egen verkstad.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Solceller och litiumbatteri i husbil – dimensionering, utbyte och priser",
          url: URL,
          inLanguage: "sv-SE",
          about: "Solceller och batterisystem i husbil",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Solceller & litium", item: URL },
          ],
        }),
      },
    ],
  }),
  component: SolcellerPage,
});

const yieldTable = [
  { m: "December", w: "30–120 Wh" },
  { m: "Mars", w: "300–550 Wh" },
  { m: "April & september", w: "400–700 Wh" },
  { m: "Juni", w: "800–1 100 Wh" },
];

function SolcellerPage() {
  return (
    <>
      <PageHeader
        label="Nationell guide · El & energi"
        title={
          <>
            Solceller som <span className="font-italic-display text-accent">räknar</span> hem sig.
          </>
        }
        sub="Vi felsöker feldimensionerade anläggningar varje säsong. Här är siffrorna vi utgår från: verkligt elbehov i wattimmar, faktiskt solutbyte månad för månad i Sverige, och vad litium kostar mot vad det ger."
      />

      <section className="container-edge pt-14 md:pt-20">
        <p className="eyebrow">Utbyte från en 200 W panel per dygn</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {yieldTable.map((r) => (
            <div key={r.m} className="rounded-3xl border border-border bg-card p-6">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{r.m}</p>
              <p className="mt-2 font-display text-3xl leading-none text-accent">{r.w}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Ren, plant takmonterad panel utan skugga. Ett normalt fritidsdygn förbrukar 500–900 Wh sommartid.
        </p>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial eyebrow="Guiden" sections={solcellerSections} faq={solcellerFaq} />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/stallplatser-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Ställplatser — priser, regler och säsong</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/fukttest-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Fukttest — takgenomföringar är största risken</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/reservdelar"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Reservdelar & tillbehör — fråga oss direkt</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
